import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import AuthShell from "../../components/layout/AuthShell";
import GoogleButton from "../../components/common/GoogleButton";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import api from "../../lib/api";
import { useAuth, formatApiError, STAFF_ROLES } from "../../context/AuthContext";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [needsVerify, setNeedsVerify] = useState(false);
  const [resending, setResending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setNeedsVerify(false);
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.name}`);
      const dest = location.state?.from || (STAFF_ROLES.includes(user.role) ? "/admin" : "/dashboard");
      navigate(dest, { replace: true });
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      setError(msg);
      if (err.response?.status === 403 && /verify/i.test(msg)) setNeedsVerify(true);
    } finally {
      setLoading(false);
    }
  };

  const resendVerification = async () => {
    if (!email) { toast.error("Enter your email above first."); return; }
    setResending(true);
    try {
      await api.post("/auth/resend-otp", { email: email.trim() });
      toast.success("Verification email sent — check your inbox.");
      navigate("/verify-otp", { state: { email: email.trim() } });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not resend. Please try again.");
    } finally {
      setResending(false);
    }
  };

  return (
    <AuthShell
      title="Sign In"
      subtitle="Access your reservations, favourites and account."
      footer={
        <p>
          New to Clacton Fireworks?{" "}
          <Link to="/register" className="text-gold hover:underline" data-testid="link-register">
            Create an account
          </Link>
        </p>
      }
    >
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div className="space-y-2">
          <Label htmlFor="email" className="text-white/80">Email</Label>
          <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="login-email-input" className="bg-navy border-white/15 text-white h-11 focus-visible:ring-gold" />
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password" className="text-white/80">Password</Label>
            <Link to="/forgot-password" className="text-xs text-gold hover:underline" data-testid="link-forgot">Forgot password?</Link>
          </div>
          <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="login-password-input" className="bg-navy border-white/15 text-white h-11 focus-visible:ring-gold" />
        </div>

        {error && <p className="text-sm text-firered" data-testid="login-error">{error}</p>}

        {needsVerify && (
          <div className="rounded-lg border border-gold/30 bg-gold/10 p-3 text-sm text-white/90 space-y-2" data-testid="login-verify-prompt">
            <p>Your email isn't verified yet. We can send you a fresh verification code.</p>
            <Button type="button" variant="outline" disabled={resending} onClick={resendVerification}
              className="w-full border-gold/40 text-gold hover:bg-gold/15" data-testid="login-resend-verify-btn">
              {resending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Resend verification email"}
            </Button>
          </div>
        )}

        <Button type="submit" disabled={loading} className="w-full cf-gold-btn font-semibold h-11" data-testid="login-submit-btn">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
        </Button>
      </form>

      <div className="my-6 flex items-center gap-4">
        <span className="h-px flex-1 bg-white/10" />
        <span className="text-xs text-muted-foreground uppercase tracking-widest">or</span>
        <span className="h-px flex-1 bg-white/10" />
      </div>

      <GoogleButton />
    </AuthShell>
  );
}
